import * as React from 'react';
import { Link } from 'react-router-dom';

import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import { Skeleton } from '@mui/material';
import MuiAppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip, { ChipProps } from '@mui/material/Chip';
import MuiLink from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

import { useAuthenticator } from '@aws-amplify/ui-react';
import { signOut as amplifySignOut } from 'aws-amplify/auth';

import { enableShopifyVenueID } from '../../../config';
import useBreakpoints from '../../../hooks/useBreakpoints';
import { useVenueUpdateVenueMutation } from '../../../hooks/reactQuery/useVenueUpdateVenueMutation';
import { useTermsAndConditions } from '../../../hooks/useTermsAndConditions';
import { extractSubscriptionPlan } from '../../../pages/SubscriptionPlans/common/helper';
import { Plan } from '../../../pages/SubscriptionPlans/types';
import { useAppReset, useAppSelector } from '../../../redux/hooks';
import { clearCurrentVenueID } from '../../../utils/current-venue';
import { VENUE_MANUAL_TAGS } from '../../../utils/venue-tags';
import Button from '../../Button/Button';
import { OnBoardingDialog } from '../../Dialogs/OnBoardingDialog/OnBoardingDialog';
import { TermsAndConditionDialog } from '../../Dialogs/TermsAndCondition/TermsAndConditionDialog';
import { UpdateToOurTermsDialog } from '../../Dialogs/UpdateToOurTerms/UpdateToOurTermsDialog';
import SvgIcon from '../../Icons';
import NotificationsBell from '../../Notifications/NotificationsBell';
import { Balance } from '../Balance';
import Menu from './Menu';
import { useProfileSwitcher } from './ProfileSwitcher';

const AppBar = styled(MuiAppBar)(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  backgroundColor: 'white',
  color: 'black',
  borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
  boxShadow: theme.shadows[1],
}));

export const mapPlanLabel: Partial<
  Record<Plan, { label: string; color: string; fill: string; bgColor: string }>
> = {
  plus: { label: 'PLUS', color: '#D86EEF', fill: 'url(#linear-gradient)', bgColor: '#FBEEFE' },
  starter: { label: 'Starter', color: '#429C12', fill: '#429C12', bgColor: '#EAF7E1' },
  basic: { label: 'Basic', color: '#1A1C1E', fill: '#1A1C1E', bgColor: '#EDEDED' },
};

export const SubscriptionPlan: React.FC<
  ChipProps & { planColor: string; fill: string; bgColor?: string }
> = ({ label, planColor, fill, bgColor, size }) => (
  <Chip
    size={size}
    icon={<SvgIcon name='Plus' sx={{ fill }} />}
    label={label}
    variant='outlined'
    color='primary'
    sx={{
      color: planColor,
      mr: 2,
      borderWidth: '1.5px',
      borderRadius: '8px',
      fontWeight: 'medium',
      borderColor: planColor,
      backgroundColor: bgColor,
      '.MuiChip-icon': {
        ml: 1,
      },
    }}
  />
);

const settings = [
  { label: 'Profile', path: 'profile' },
  { label: 'Balance', path: 'balance' },
  { label: 'Premium Plans', path: 'plans' },
];

const ONBOARDED_STRING = 'onboardedWeb';

const Header: React.FC = () => {
  const { isCompact } = useBreakpoints();
  const termsData = useTermsAndConditions();
  const { subscription } = useAppSelector((state) => state.subscription.value);
  const { value, isLoading } = useAppSelector((state) => state.balance);

  const venueUpdateMutate = useVenueUpdateVenueMutation({});

  const { balance, status } = value || {};

  const { signOut } = useAuthenticator();
  const venueState = useAppSelector((state) => state.venue.value);
  const resetApp = useAppReset();

  const [showTermsAndConditionDialog, setShowTermsAndConditionDialog] = React.useState(false);
  const [showTermsAndConditionDialogV2, setShowTermsAndConditionDialogV2] = React.useState(false);
  const [showOnBoardingFlowDialog, setShowOnBoardingFlowDialog] = React.useState(true);

  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(null);

  const tncVersion = venueState?.tncVersion;

  const tncUpdateRequired = Boolean(
    termsData?.updateDate &&
      (!venueState?.termsAccepted || !tncVersion || termsData?.updateDate !== tncVersion),
  );

  React.useEffect(() => {
    if (venueState) {
      if (venueState?.flags?.includes(ONBOARDED_STRING)) {
        setShowOnBoardingFlowDialog(false);
      } else {
        setShowOnBoardingFlowDialog(true);
      }
    }
  }, [venueState]);

  React.useEffect(() => {
    setShowTermsAndConditionDialog(tncUpdateRequired);
  }, [tncUpdateRequired]);

  const handleOpenUserMenu = React.useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  }, []);

  const handleOpenTermsAndCondition = React.useCallback(() => {
    setShowTermsAndConditionDialogV2((prev) => !prev);
  }, []);

  const handleCloseUserMenu = React.useCallback(() => {
    setAnchorElUser(null);
  }, []);

  const { profileMenuItems, profileDialogs } = useProfileSwitcher(handleCloseUserMenu);

  const handleOnBoardingFlow = React.useCallback(() => {
    venueUpdateMutate.mutate({
      id: String(venueState?.id),
      onboardedWeb: true,
    });
    setShowOnBoardingFlowDialog(false);
  }, [venueState?.id, venueUpdateMutate]);

  const onSignout = React.useCallback(async () => {
    await amplifySignOut();
    signOut();
    handleCloseUserMenu();

    localStorage.setItem('lastLoggedInVenueID', JSON.stringify(venueState?.id));

    clearCurrentVenueID();
    window.localStorage.removeItem('collabReducer');
    window.localStorage.removeItem('collab-search-text');
    window.localStorage.removeItem('product-form-data');
    window.localStorage.removeItem('product-form-step');
    window.localStorage.removeItem('offer-form-data');
    window.localStorage.removeItem('offer-form-step');

    setTimeout(() => {
      resetApp();
    }, 500);
  }, [signOut, handleCloseUserMenu, resetApp]);

  const subscriptionPlan = React.useMemo(() => {
    const [plan] = extractSubscriptionPlan(subscription?.sku ?? '');

    return plan;
  }, [subscription]);

  const isFreeUser = !subscription?.sku;
  const isOnlineBrand = venueState?.tags?.includes(VENUE_MANUAL_TAGS.Online) || false;

  const upgradeCta = React.useMemo(() => {
    if (subscriptionPlan === 'plus') return null;
    if (
      venueState?.tags?.includes(VENUE_MANUAL_TAGS.BrandPlans) &&
      subscriptionPlan === 'starter'
    ) {
      return null;
    }

    if (isFreeUser && isOnlineBrand) {
      return {
        label: 'Start receiving promotions',
        shortLabel: 'Promotions',
        background: '#EDEDED',
        hoverBackground: '#DCDCDC',
        color: '#1A1C1E',
        iconFill: '#1A1C1E',
        borderColor: 'transparent',
        shadow: 'none',
      };
    }

    if (subscriptionPlan === 'starter') {
      return {
        label: 'Upgrade to PLUS',
        shortLabel: 'PLUS',
        background: 'transparent',
        hoverBackground: 'rgba(216, 110, 239, 0.08)',
        color: '#D86EEF',
        iconFill: '#D86EEF',
        borderColor: '#D86EEF',
        shadow: 'none',
      };
    }

    return {
      label: 'Upgrade to Starter',
      shortLabel: 'Starter',
      background: 'transparent',
      hoverBackground: 'rgba(63, 168, 11, 0.08)',
      color: '#3FA80B',
      iconFill: '#3FA80B',
      borderColor: '#3FA80B',
      shadow: 'none',
    };
  }, [subscriptionPlan, isFreeUser, isOnlineBrand, venueState?.tags]);

  return (
    <AppBar position='relative' sx={{ boxShadow: 'rgba(0, 0, 0, 0.02) 0px 1px 6px 0px' }}>
      <Toolbar disableGutters sx={{ px: isCompact ? 2 : 3, minWidth: 0 }}>
        <Typography
          component='div'
          variant='body2'
          fontWeight='medium'
          sx={{ display: 'flex', alignItems: 'center', color: '#B4B4B4', flexShrink: 0 }}
        >
          <Box component={Link} sx={{ mr: 1.5, display: 'flex' }}>
            <img height={30} src='/logo-header.png' />
          </Box>
        </Typography>

        <Box sx={{ flexGrow: 1 }}></Box>

        <Box
          display='flex'
          alignItems='center'
          sx={{ flexGrow: 0, minWidth: 0, gap: isCompact ? '8px' : '16px' }}
        >
          {enableShopifyVenueID &&
          venueState &&
          String(venueState?.id) === String(enableShopifyVenueID) ? null : (
            <>
              {upgradeCta && (
                <Button
                  component={Link}
                  to='/plans'
                  id='btn-premium-plans'
                  startIcon={
                    <SvgIcon
                      name='Plus'
                      sx={{
                        fill: upgradeCta.iconFill,
                        fontSize: '14px',
                        width: '14px',
                        height: '14px',
                      }}
                    />
                  }
                  variant='contained'
                  sx={{
                    background: upgradeCta.background,
                    color: upgradeCta.color,
                    boxShadow: upgradeCta.shadow,
                    border:
                      upgradeCta.borderColor && upgradeCta.borderColor !== 'transparent'
                        ? `1.5px solid ${upgradeCta.borderColor}`
                        : 'none',
                    minHeight: '39px',
                    px: isCompact ? 1.25 : 2,
                    fontSize: isCompact ? '14px' : '15px',
                    fontWeight: 500,
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    '& .MuiButton-startIcon': {
                      mr: 0.6,
                      '& > *:nth-of-type(1)': {
                        fontSize: '14px',
                        width: '14px',
                        height: '14px',
                      },
                    },
                    '&:hover': {
                      background: upgradeCta.hoverBackground,
                      boxShadow: upgradeCta.shadow,
                      border:
                        upgradeCta.borderColor && upgradeCta.borderColor !== 'transparent'
                          ? `1.5px solid ${upgradeCta.borderColor}`
                          : 'none',
                    },
                  }}
                >
                  {isCompact ? upgradeCta.shortLabel : upgradeCta.label}
                </Button>
              )}
            </>
          )}

          {isLoading ? (
            <Skeleton width={'94px'} height={'65px'} />
          ) : (
            <Box
              component={Link}
              to={'/balance'}
              sx={{
                textDecoration: 'none',
              }}
            >
              <Balance
                currency={venueState?.currency || ''}
                balance={balance || 0}
                status={status}
              />
            </Box>
          )}

          <NotificationsBell />

          <Tooltip title='Open settings'>
            <Chip
              onClick={handleOpenUserMenu}
              avatar={<Avatar alt='avatar' src={venueState?.picture?.thumbnail || ''} />}
              label={
                <Box display='flex' alignItems='center' gap='6px'>
                  {venueState?.name && (
                    <Typography
                      fontSize='14px'
                      fontWeight={500}
                      noWrap
                      sx={{ maxWidth: isCompact ? '110px' : '180px' }}
                    >
                      {venueState.name}
                    </Typography>
                  )}
                  <NotesOutlinedIcon />
                </Box>
              }
              variant='outlined'
              sx={{
                py: 2.4,
                px: 0,
                '.MuiChip-avatar': {
                  height: '32px',
                  width: '32px',
                },
                borderRadius: '36px',
              }}
            />
          </Tooltip>
          <Menu
            sx={{ mt: '45px' }}
            id='menu-appbar'
            anchorEl={anchorElUser}
            open={Boolean(anchorElUser)}
            onClose={handleCloseUserMenu}
          >
            {profileMenuItems}
            {settings
              .filter((x) => {
                if (
                  x.path === 'plans' &&
                  enableShopifyVenueID &&
                  String(venueState?.id) === String(enableShopifyVenueID)
                ) {
                  return false;
                }
                return true;
              })
              .map((x) => (
                <MenuItem divider key={x.path} onClick={handleCloseUserMenu}>
                  <MuiLink
                    component={Link}
                    to={x.path}
                    width='100%'
                    underline='none'
                    fontWeight='bold'
                    sx={{ paddingY: '6px' }}
                  >
                    {x.label}
                  </MuiLink>
                </MenuItem>
              ))}
            <MenuItem divider onClick={handleOpenTermsAndCondition}>
              <Typography fontWeight='bold' sx={{ paddingY: '6px', fontWeight: 'bold' }}>
                Terms & Conditions
              </Typography>
            </MenuItem>
            <MenuItem onClick={onSignout} sx={{ marginTop: '32px' }}>
              <Typography textAlign='center'>Logout</Typography>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
      <UpdateToOurTermsDialog
        isTermsAccepted={!tncUpdateRequired || false}
        open={showTermsAndConditionDialog}
        disableEscapeKeyDown
        onClose={() => {
          return;
        }}
        onSuccess={() => {
          setShowTermsAndConditionDialog(false);
        }}
        termsData={termsData}
      />
      <TermsAndConditionDialog
        showAgreementsButtons={!venueState?.termsAccepted || false}
        termsData={termsData}
        open={showTermsAndConditionDialogV2}
        onClose={handleOpenTermsAndCondition}
      />

      <OnBoardingDialog
        disableEscapeKeyDown
        open={showOnBoardingFlowDialog}
        onClose={() => {
          return;
        }}
        venue={venueState}
        handleSkip={() => {
          handleOnBoardingFlow();
        }}
        handleLetsStart={() => {
          handleOnBoardingFlow();
        }}
      />
      {profileDialogs}
    </AppBar>
  );
};
export default Header;
